import * as React from 'react';
// Import mui modules.
import {
	EditOutlined,
} from '@mui/icons-material';
// Import icon components.
import {
	DeleteIcon,
	ReinstateIcon,
} from '@app/components/view/icons';
// Import props.
import {
	ComparativeEntitySummaryProps,
	ComparativeDataHeaderProps,
    IconButtonProps,
} from '@app/props';
// Import enumerations.
import {
    AppThemeColor,
} from '@app/enumerations';

interface UseComparativeEntitySummaryParameters<TEntity> {
	id: string;
	props: ComparativeEntitySummaryProps<TEntity>;
}

interface UseComparativeEntitySummaryResults {
	baseId: string;
	entityId: string;
	isEntityAdded: boolean;
	isEntityRemoved: boolean;
	isSubheaderVisible: boolean;
	isErrorHeaderVisible: boolean;
	applyEntityComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyEntityComparativeSubHeaderProps: () => ComparativeDataHeaderProps;
	applyEntityErrorStatusComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
}

const useComparativeEntitySummary = <TEntity>(params: UseComparativeEntitySummaryParameters<TEntity>): UseComparativeEntitySummaryResults => {

	// #region exported methods

	/**
	 * Returns entity comparative data header props. 
	 **/
	const applyEntityComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${baseId}-header`,
			isSticky: isHeaderSticky,
			title: !String.isNullOrWhiteSpace(params.props.comparisonEntitySummary.descriptor)
				? params.props.comparisonEntitySummary.descriptor.toTitleCase() 
				: params.props.defaultTitle.toTitleCase(),
			titlePostFix: isEntityAdded ? '(Added)' : (isEntityRemoved ? '(Removed)' : undefined),
			titlePostFixColor: isEntityAdded ? AppThemeColor.SUCCESS : (isEntityRemoved ? AppThemeColor.ERROR : undefined),
			titlePostFixFontSize: 'xsm',
			titleFontSize: 'sm',
			iconButtons: getHeaderIconButtons(),
		};
	};

	/**
	 * Returns entity comparative data subheader props, this displays any provided sub-descriptor. 
	 **/
	const applyEntityComparativeSubHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${baseId}-status-header`,
			isSticky: isHeaderSticky,
			title: `${params.props.comparisonEntitySummary.subDescriptor}`,
			titleColor: AppThemeColor.INFO,
			titleFontSize: 'xxsm',
			titleFontStyle: 'italic',
			titleFontWeight: 'normal',
		};
	};

	/**
	 * Returns entity error status comparative data header props. 
	 **/
	const applyEntityErrorStatusComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${baseId}-status-header`,
			isSticky: true,
			title: 'Incomplete - Additional Information Required',
			titleColor: AppThemeColor.ERROR,
			titleFontSize: 'xxsm',
			titleFontStyle: 'italic',
			titleFontWeight: 'normal',
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Fetches the header icons.
	 */
	const getHeaderIconButtons = (): IconButtonProps[] => {
		let iconButtons: IconButtonProps[] = [];

		if (isEntityRemoved) {
			iconButtons.push({
				label: 'Reinstate',
				onClick: () => params.props.entityActionProps.onReinstateEntity(entityId),
				icon: ReinstateIcon,
			});
		}
		else {
			if (allowEntityEdits) {
				iconButtons.push({
					label: 'Edit',
					onClick: () => params.props.entityActionProps.onEditEntity(entityId),
					icon: EditOutlined,
					ref: editButtonRef
				});
			}
			if (allowEntityRemoval) {
				iconButtons.push({
					label: 'Remove',
					onClick: () => params.props.entityActionProps.onRemoveEntity(entityId),
					icon: DeleteIcon,
				});
			}
		}

		return iconButtons;
	}

	// #endregion private methods


	// #region variables

	const entityId: string = React.useMemo(
		() => params.props.comparisonEntitySummary.id ?? String.empty(),
		[params.props.comparisonEntitySummary],
	);
	const baseId: string = React.useMemo(
		() => `${params.id}-${entityId}`,
		[params.id, entityId],
	);
	const isEntityAdded: boolean = React.useMemo(
		() => params.props.comparisonEntitySummary.oldData === undefined && params.props.comparisonEntitySummary.newData !== undefined,
		[params.props.comparisonEntitySummary.oldData, params.props.comparisonEntitySummary.newData],
	);
	const isEntityRemoved: boolean = React.useMemo(
		() => params.props.comparisonEntitySummary.oldData !== undefined && params.props.comparisonEntitySummary.newData === undefined,
		[params.props.comparisonEntitySummary.oldData, params.props.comparisonEntitySummary.newData],
	);
	const allowEntityEdits: boolean = React.useMemo(
		() => params.props.comparisonEntitySummary.allowEdits,
		[params.props.comparisonEntitySummary]
	);
	const allowEntityRemoval: boolean = React.useMemo(
		() => params.props.comparisonEntitySummary.allowRemoval,
		[params.props.comparisonEntitySummary]
	);
	const isHeaderSticky: boolean = React.useMemo(
		() => params.props.isHeaderSticky ?? false,
		[params.props.isHeaderSticky]
	);
	const isSubheaderVisible: boolean = React.useMemo(
		() => !String.isNullOrWhiteSpace(params.props.comparisonEntitySummary.subDescriptor),
		[params.props.comparisonEntitySummary.subDescriptor]
	);
	const isErrorHeaderVisible: boolean = React.useMemo(
		() => params.props.comparisonEntitySummary.requiresReview && allowEntityEdits,
		[params.props.comparisonEntitySummary.requiresReview, allowEntityEdits]
	);

	const focusToErrorOnMount: boolean = React.useMemo(() => params.props.focusToErrorOnMount ?? false, [params.props.focusToErrorOnMount]);
	const editButtonRef: React.RefObject<HTMLButtonElement> = React.useRef<HTMLButtonElement>(null);

	// #endregion private variables

	// #region life-cycle

	React.useEffect(() => {	
		if (focusToErrorOnMount
			&& editButtonRef.current !== null) {
			editButtonRef.current.focus();
		}
	}, []);

	// #endregion life-cycle

	return {
		baseId,
		entityId,
		isEntityAdded,
		isEntityRemoved,
		isSubheaderVisible,
		isErrorHeaderVisible,
		applyEntityComparativeDataHeaderProps,
		applyEntityComparativeSubHeaderProps,
		applyEntityErrorStatusComparativeDataHeaderProps,
	};
}

// Export parameters.
export {
	UseComparativeEntitySummaryParameters,
}

// Export results.
export {
	UseComparativeEntitySummaryResults,
}

export default useComparativeEntitySummary;